/* eslint-disable no-unused-vars, valid-jsdoc */
import React, { PureComponent } from 'react';
import { keyName } from '@bingads-webui/keycode';
import { withLiveRegionProp } from '@bingads-webui-react/hoc-with-live-region';
import type { SearchBoxProps, SearchResultLiveRegionProps } from './types';

/**
 * SearchBox component provides an input field for searching through content
 */
export class SearchBox extends PureComponent<SearchBoxProps> {
  static defaultProps: Partial<SearchBoxProps> = {
    maxLength: 150,
    ariaLabel: '',
    placeholder: '',
    isDisabled: false,
    iconClasses: [],
    searchBoxClasses: [],
    onFocus: (_value: string) => {},
    onBlur: (_value: string) => {},
  };

  onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // enter
    if (event.key === keyName[13]) {
      event.preventDefault();
      this.props.onSearch(this.props.value);
    }

    // escape
    if (event.key === keyName[0x1B]) {
      event.preventDefault();
      this.props.onSearch('');
    }
  };

  onClick = (event: React.SyntheticEvent<HTMLSpanElement>) => {
    event.preventDefault();
    this.props.onSearch(this.props.value);
  };

  onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.props.onQueryChange(event.target.value);
  };

  onFocusCapture = (event: React.FocusEvent<HTMLInputElement>) => {
    this.props.onFocus(event.target.value);
  };

  onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    this.props.onBlur(event.target.value);
  };

  render() {
    return (
      <div className={`ba-search-box ${this.props.searchBoxClasses.join(' ')}`}>
        <div className="search-box__input-group">
          <input
            className="search-box__input form-control"
            type="text"
            maxLength={this.props.maxLength}
            placeholder={this.props.placeholder}
            onKeyPress={this.onKeyPress}
            value={this.props.value}
            onChange={this.onChange}
            aria-label={this.props.ariaLabel || this.props.placeholder}
            disabled={this.props.isDisabled}
            onFocusCapture={this.onFocusCapture}
            onBlur={this.onBlur}
          />

          <span
            className={`search-box__icon ${this.props.iconClasses.join(' ')}`}
            tabIndex={0}
            role="button"
            onClick={this.onClick}
            onKeyPress={this.onClick}
            aria-label={this.props.ariaLabel || this.props.placeholder}
          />
        </div>
      </div>
    );
  }
}

export const SearchBoxWithLiveResults = withLiveRegionProp(SearchBox);

export const getSearchResultLiveRegionText = (totalCount: number, emptyText = '', resultText = '') =>
  (totalCount === 0 ? emptyText : resultText);

export const SearchResultLiveRegion = (props: SearchResultLiveRegionProps) => {
  const {
    totalCount, 'aria-live': ariaLive, emptyText, resultText, role,
  } = props;
  const liveRegionText = getSearchResultLiveRegionText(totalCount, emptyText, resultText);

  return (
    <div
      className="search-result live-region"
      aria-live={ariaLive}
      aria-label={liveRegionText}
      role={role}
    />
  );
};

SearchResultLiveRegion.defaultProps = {
  'aria-live': 'assertive',
  emptyText: '',
  resultText: '',
  role: 'region',
};
